/**
 * Shared helpers for resolving assets against the static FILES host.
 *
 * Why this exists:
 * - Multiple UI atoms need to resolve root-relative assets ("/images/x.svg")
 * against a deployment-specific files origin.
 * - Client components can only reliably access NEXT_PUBLIC_* env vars at runtime.
 */

export const DEFAULT_FILES_HOST = 'https://mneno-files.theai.app';

function normalizeOrigin(origin: string): string {
  return String(origin ?? '').trim().replace(/\/+$/, '');
}

/**
 * Determine the files origin.
 *
 * Priority:
 * 1) NEXT_PUBLIC_FILES_HOST / NEXT_PUBLIC_FILES_ORIGIN (client-safe)
 * 2) FILES_HOST (server-only, included as best-effort)
 * 3) DEFAULT_FILES_HOST fallback
 */
export function getFilesOrigin(): string {
  const fromEnv = normalizeOrigin(
    String(
      process.env.NEXT_PUBLIC_FILES_HOST ??
        process.env.NEXT_PUBLIC_FILES_ORIGIN ??
        // Server-only best-effort fallback (will be undefined in the browser bundle).
        process.env.FILES_HOST ??
        '',
    ),
  );

  // Minimal validation: require http(s) if provided.
  if (fromEnv && /^https?:\/\//i.test(fromEnv)) return fromEnv;

  return DEFAULT_FILES_HOST;
}

export function isProbablyAbsoluteUrl(input: string): boolean {
  const s = String(input ?? '').trim().toLowerCase();
  if (!s) return false;

  if (s.startsWith('http://') || s.startsWith('https://')) return true;
  if (s.startsWith('data:') || s.startsWith('blob:')) return true;

  // Protocol-relative URLs are already “absolute enough” for the browser.
  if (s.startsWith('//')) return true;

  return false;
}

/**
 * Minimal URL hardening:
 * - Prevent obvious `javascript:` injection vectors.
 * - Return empty string when unsafe.
 */
export function sanitizeUrl(input: string): string {
  const raw = String(input ?? '').trim();
  if (!raw) return '';

  const lowered = raw.toLowerCase();
  if (lowered.startsWith('javascript:')) return '';

  return raw;
}

export type OptimizedImageFormat = 'webp' | 'avif' | 'png' | 'jpeg';

/** Raster extensions that the files host can re-encode. */
const RASTER_IMAGE_EXTENSIONS = /\.(?:png|jpe?g|webp|avif)$/i;

export function isRasterImagePath(src: string): boolean {
  return RASTER_IMAGE_EXTENSIONS.test(String(src ?? '').trim());
}

export interface ImageDerivativeOptions {
  /** Requested derivative width in pixels. */
  width?: number;
  /** Output format; webp is the default. */
  format?: OptimizedImageFormat;
  /** Not supported by the path-based API; requests specifying quality are not rewritten. */
  quality?: number;
}

/**
 * Build a files-host derivative URL at /<width>/<key>.<format>.
 * For example, /mental2/logo-mark.png at width 76 in webp format becomes
 * /76/mental2/logo-mark.webp.
 *
 * Returns null when the source is not an eligible root-relative raster image,
 * no valid width is available, or a quality setting cannot be represented.
 */
export function buildImageDerivativeUrl(
  src: string,
  filesOrigin: string,
  options: ImageDerivativeOptions = {},
): string | null {
  const raw = String(src ?? '').trim();
  if (!raw || !raw.startsWith('/') || isProbablyAbsoluteUrl(raw)) return null;
  if (!isRasterImagePath(raw)) return null;

  const key = raw.replace(/^\/+/, '');
  if (!key || key.includes('..')) return null;

  const width = options.width;
  if (typeof width !== 'number' || !Number.isFinite(width)) return null;
  const roundedWidth = Math.round(width);
  if (!Number.isSafeInteger(roundedWidth) || roundedWidth < 1) return null;
  if (options.quality !== undefined) return null;

  const format = options.format ?? 'webp';
  if (!/^(?:webp|avif|png|jpeg)$/.test(format)) return null;

  const derivativeKey = key.replace(RASTER_IMAGE_EXTENSIONS, `.${format}`);
  return `${normalizeOrigin(filesOrigin)}/${roundedWidth}/${derivativeKey}`;
}

export interface ResolveAssetUrlOptions extends ImageDerivativeOptions {
  /**
   * When true, eligible root-relative raster images are routed through the
   * width-prefixed derivative path instead of served as the original file.
   */
  optimizeImage?: boolean;
}

/**
 * Resolve an asset URL:
 * - If src is root-relative ("/images/x.svg"), rewrite to "<filesOrigin>/images/x.svg"
 * - If optimizeImage is set and src is an eligible raster image with a width,
 * rewrite to the files-host derivative path.
 * - If src is absolute (https://...), keep as-is
 * - Other relative paths are returned unchanged (best-effort).
 *
 * Note: This function does not sanitize; callers may additionally apply `sanitizeUrl`.
 */
export function resolveAssetUrl(
  src: string,
  filesOrigin: string,
  options: ResolveAssetUrlOptions = {},
): string {
  const raw = String(src ?? '').trim();
  if (!raw) return '';

  if (isProbablyAbsoluteUrl(raw)) return raw;

  // Only rewrite root-relative paths.
  if (raw.startsWith('/')) {
    if (options.optimizeImage) {
      const derivative = buildImageDerivativeUrl(raw, filesOrigin, options);
      if (derivative) return derivative;
    }
    return `${filesOrigin}${raw}`;
  }

  return raw;
}